import { ArrowLeft, Loader2 } from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  Button,
  Card,
  CardContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components";
import { useI18n } from "@/context/i18n";

import { useCampanhaUpload } from "./campaign-upload.hooks";
import {
  CampaignUploadDropzone,
  CampaignUploadPreviewTable,
  CampaignUploadSummaryCards,
} from "./components";

export const CampaignUploadPage: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { campaignId, clientId } = useParams<{
    campaignId: string;
    clientId: string;
  }>();
  const {
    form,
    selectedFile,
    previewRows,
    uploadSummary,
    isUploading,
    isParsingFile,
    isDownloadingTemplate,
    downloadTemplate,
    handleFileSelect,
    handleSubmit,
  } = useCampanhaUpload(campaignId, clientId);

  const handleBack = React.useCallback(() => {
    if (clientId) {
      navigate(`/clients/${clientId}/campaigns`);
      return;
    }

    navigate("/clients");
  }, [clientId, navigate]);

  return (
    <div className="flex flex-col w-full h-full space-y-6 p-4">
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="p-3"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t("campaigns.upload.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("campaigns.upload.description")}
          </p>
        </div>
      </div>

      <Card className="p-4">
        <CardContent>
          <div className="mb-4">
            <Button
              type="button"
              variant="outline"
              onClick={downloadTemplate}
              disabled={isDownloadingTemplate}
            >
              {isDownloadingTemplate && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("campaigns.upload.form.downloadTemplate")}
            </Button>
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="file"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <CampaignUploadDropzone
                        onFileSelected={handleFileSelect}
                        selectedFile={selectedFile}
                        disabled={isUploading || isParsingFile}
                        title={t("campaigns.upload.form.title")}
                        description={t("campaigns.upload.form.description")}
                      />
                    </FormControl>
                    <FormMessage>
                      {form.formState.errors.file?.message
                        ? t(String(form.formState.errors.file?.message))
                        : undefined}
                    </FormMessage>
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full"
                disabled={!selectedFile || isUploading || isParsingFile}
              >
                {(isUploading || isParsingFile) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {isUploading || isParsingFile
                  ? t("campaigns.upload.processing")
                  : t("campaigns.upload.form.submit")}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>

      {uploadSummary && (
        <CampaignUploadSummaryCards
          summary={uploadSummary}
          labels={{
            valid: t("campaigns.upload.summary.valid"),
            invalid: t("campaigns.upload.summary.invalid"),
            duplicate: t("campaigns.upload.summary.duplicate"),
            inserted: t("campaigns.upload.summary.inserted"),
          }}
        />
      )}

      <CampaignUploadPreviewTable
        rows={previewRows}
        title={t("campaigns.upload.preview.title")}
        noRowsLabel={t("campaigns.upload.preview.empty")}
        headers={{
          line: t("campaigns.upload.preview.columns.line"),
          company: t("campaigns.upload.preview.columns.company") || "Empresa",
          first_name: t("campaigns.upload.preview.columns.first_name") || "Nome",
          last_name: t("campaigns.upload.preview.columns.last_name") || "Sobrenome",
          email: t("campaigns.upload.preview.columns.email") || "E-mail",
          phone: t("campaigns.upload.preview.columns.phone"),
          phone_2: t("campaigns.upload.preview.columns.phone_2") || "Telefone 2",
          document_number: t("campaigns.upload.preview.columns.document_number") || "CNPJ/CPF",
          status: t("campaigns.upload.preview.columns.status"),
          reason: t("campaigns.upload.preview.columns.reason"),
        }}
        statusLabels={{
          valid: t("campaigns.upload.status.valid"),
          invalid: t("campaigns.upload.status.invalid"),
          duplicate: t("campaigns.upload.status.duplicate"),
        }}
      />
    </div>
  );
};
